// @ts-strict-ignore
import React, { useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Button, ButtonVariant, IconName, Spinner,
} from '@usealloy/component-library';

import AlloyLogoBlack from 'resources/images/alloy-logo-v2-black.svg';
import { UserContext } from '~/dashboard/context';
import NotificationDropdown from '~/dashboard/components/notifications/NotificationDropdown';
import AccountDropdown from '~/dashboard/components/AccountDropdown';
import { useReturnToAdminDashboard } from '~/dashboard/components/useReturnToAdminDashboard';
import { useAppSelector } from '~/store/reduxHooks';
import { selectInvestigationDetail, selectInvestigationFetched } from '~/investigations/redux/selectors';

function Topbar() {
  const { agent, breadcrumbs, customer } = useContext(UserContext);
  const [employeeRole, setEmployeeRole] = useState('');

  useEffect(() => {
    if (agent.adminAgentCustomerRole) {
      setEmployeeRole(`Alloy ${agent.adminAgentCustomerRole}`);
    }
  }, [agent]);

  const location = useLocation();
  const inAdminDashboard = location.pathname.startsWith('/v3/dashboard/admin');
  const inNotificationView = location.pathname.startsWith('/v3/dashboard/notifications');

  const getOrganizationName = () => {
    if (inAdminDashboard) {
      return agent.isEmployee
        ? 'Alloy Internal Dashboard'
        : (customer?.organizationName || agent.customerAgents?.[0]?.organizationName);
    }
    return customer.organizationName;
  };

  const shouldShowBackButton = (
    agent.isEmployee
    || (
      agent.isAfefParentAgent
      && (customer.isAfefChildOrganization || customer.isAfefParentOrganization) // don't show back button if the current customer is not an afef customer, even if the agent is a parent agent
    )
  );

  const inInvestigationDetailView = location.pathname.includes('/INV-');
  const investigation = useAppSelector(selectInvestigationDetail);
  const investigationFetched = useAppSelector(selectInvestigationFetched);
  const isInvestigationArchivedOrClosed = (
    inInvestigationDetailView
    && investigationFetched
    && Boolean(investigation?.investigationArchivedAt || investigation?.investigationClosedAt)
  );

  return (
    <nav
      id="topbar"
      className="flex space no-wrap small-header"
      style={{
        background: isInvestigationArchivedOrClosed && 'var(--cl-gray-200)',
      }}
    >
      <span className="flex start align-end smaller-header-space">
        <img alt="alloy-logo" src={AlloyLogoBlack.toString()} />
        {/* if the index is the last in the breadcrumbs length, add a test id  */}
        {breadcrumbs && !!breadcrumbs.length && (
          <span className="breadcrumbs flex start tiny-header-space" data-is-spa="true">
            {breadcrumbs.map((breadcrumb, idx) => (
              <>
                <span>/</span>
                {idx === breadcrumbs.length - 1
                  ? <span className="secondary inline">{breadcrumb.text}</span>
                  : (
                    <a
                      className="secondary inline"
                      href={breadcrumb.href}
                      // this is for playwright locator in pages/workflowsListPage/locators.ts
                      {...(breadcrumbs.length >= 2 && idx === breadcrumbs.length - 2 ? { 'data-testid': 'last-workflow-breadcrumb' } : {})}
                    >
                      {breadcrumb.text}
                    </a>
                  )}
              </>
            ))}
          </span>
        )}
      </span>

      <ul className="nav no-flex-wrap">
        {!inAdminDashboard && (
          shouldShowBackButton ? (
            <BackButton />
          ) : (
            <NotificationDropdown inNotificationView={inNotificationView} />
          )
        )}

        <AccountDropdown />

        <li className="account dashboard-account flex flex-col start align-start no-wrap small-text">
          <span className="user bold" data-dd-privacy="allow">
            {agent.isAgent ? `${agent.firstName[0]}. ${agent.lastName}` : employeeRole}
          </span>

          <span id="current-organization-name" className="organization grey" data-dd-privacy="allow">
            {getOrganizationName()}
          </span>
        </li>
      </ul>
    </nav>
  );
}

function BackButton() {
  const { returnToAdminDashboard, isLoading } = useReturnToAdminDashboard();
  return (
    <li className="flex">
      {isLoading
        ? <Spinner style={{ marginTop: 'var(--cl-space-2)' }} />
        : (
          <Button
            className="dropdown-button"
            title="Return to Admin dashboard"
            variant={ButtonVariant.tertiary}
            icon={IconName.ArrowLeft}
            onClick={returnToAdminDashboard}
          />
        )}
    </li>
  );
}


export default Topbar;
